<template>
  <div data-component="StartDateForm">
    <h2 class="gl_form-title">
      {{ title }}
    </h2>

    <CardInfoComponent
      icon="icon-informacion"
      v-if="startDateInfoCard"
      textAlign="justify"
    >
      {{ startDateInfoCard.label }}
    </CardInfoComponent>

    <RadioButtonGroup
      id="main"
      :options="options"
      :value="selectChoice"
      v-model="selectChoice"
      :rules="[validationRules.radioRequired]"
    >
      <template
        v-if="selectChoice === StartDateEnum.ANOTHER"
        v-slot:another
      >
        <CardInfoComponent
          :icon="configurations.startDateHint?.icon"
          v-if="configurations.startDateHint"
        >
          {{ configurations.startDateHint?.label }}
        </CardInfoComponent>
        <DatePickerComponent
          id="startDate"
          :label="t('rater.common.start-date.another.label')"
          :hint="
            t('rater.common.start-date.another.hint', {
              min: minDateHint,
              max: maxDateHint
            })
          "
          v-model="startDate"
          :datePickerVisible="true"
          :min-date="minDate"
          :max-date="maxDate"
          :rules="[required]"
        />
      </template>
    </RadioButtonGroup>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'

  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import DatePickerComponent from '@/modules/core/components/ui/DatePickerComponent.vue'
  import RadioButtonGroup from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import { addDaysToDate, format } from '@/utils/date.utils'
  import { useI18n } from 'vue-i18n'

  import type { OptionsValuesInterface } from '@/modules/core/models/types'

  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import BranchEnum from '@/modules/core/models/BranchEnum'
  import { ForWhomEnum, StartDateEnum } from '@/modules/core/models/enums'
  import { useCommonStore } from '@/modules/core/stores'

  const { t } = useI18n()
  const { required } = useRules()
  const commonStore = useCommonStore()

  defineExpose({ triggerSubmit })

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
    extra?: Record<string, any>
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})

  const isForeignHealth = props.configurations.businessLine === BranchEnum.FOREIGN_HEALTH

  // const MIN_DATE = addDaysToDate(props.extra?.minDays ?? 1)
  const MIN_DATE = isForeignHealth
    ? new Date(new Date().setHours(0, 0, 0, 0))
    : addDaysToDate(props.extra?.minDays ?? 1)
  const TODAY_DATE = new Date()
  const TOMORROW_DATE = addDaysToDate(isForeignHealth ? 0 : 1)

  const validationRules = {
    radioRequired: (value: string) => required(value, 'common.validations.option')
  }

  const maxDate = ref(addDaysToDate(props.configurations.maxDays))
  const title = computed(() => {
    const suffix = props.answers.forWhom === ForWhomEnum.ME ? ForWhomEnum.ME : ForWhomEnum.NOT_ME

    return t(`copy_common_start-date-form_title-${suffix}`)
  })
  const minDateHint = computed(() =>
    format(minDate.value, 'es', {
      month: 'long',
      day: 'numeric'
    })
  )
  const maxDateHint = computed(() =>
    format(maxDate.value, 'es', {
      month: 'long',
      day: 'numeric'
    })
  )
  const minDate = computed(() => {
    const today = new Date(MIN_DATE)
    return new Date(today.getFullYear(), today.getMonth(), today.getDate())
  })

  const options: OptionsValuesInterface[] = isForeignHealth
    ? [
        {
          label: t('rater.common.start-date.options.today', {
            date: TODAY_DATE.toLocaleDateString('en-GB')
          }),
          value: StartDateEnum.TODAY
        },
        {
          label: t('rater.common.start-date.options.another'),
          value: StartDateEnum.ANOTHER
        }
      ]
    : [
        {
          label: t('rater.common.start-date.options.tomorrow', {
            date: TOMORROW_DATE.toLocaleDateString('en-GB')
          }),
          value: StartDateEnum.TOMORROW
        },
        {
          label: t('rater.common.start-date.options.another'),
          value: StartDateEnum.ANOTHER
        }
      ]

  const startDate = ref(props.answers.startDate)
  const isToday =
    isForeignHealth && (!startDate.value || format(startDate.value) === format(TODAY_DATE))
  const isTomorrow =
    !isForeignHealth && (!startDate.value || format(startDate.value) === format(MIN_DATE))

  const selectChoice = ref(startValue())

  watch(selectChoice, (newChoice) => {
    if (newChoice === StartDateEnum.ANOTHER && !startDate.value) {
      startDate.value = TOMORROW_DATE
    }
  })

  const startDateInfoCard = computed(() => {
    if (!isForeignHealth) {
      return props.configurations.startDateHeaderHint
    }

    const hasMediator = !!commonStore.infoMediator
    const mediatorPhone = commonStore.infoMediator?.mediatorPhone || ''
    const mediatorAlert = props.configurations.tags?.durationAlertResidentMediator
    const defaultAlert = props.configurations.tags?.durationAlertResident

    if (hasMediator && mediatorAlert) {
      return {
        ...mediatorAlert,
        label: mediatorPhone
          ? mediatorAlert.label.replace(/\{phone\}/g, mediatorPhone)
          : mediatorAlert.label
      }
    }

    return defaultAlert
  })

  function startValue() {
    if (isToday) {
      return StartDateEnum.TODAY
    }
    if (isTomorrow) {
      return StartDateEnum.TOMORROW
    }
    return StartDateEnum.ANOTHER
  }

  function triggerSubmit() {
    const page = props.nextForm[0]
    const answers = {
      startDate:
        selectChoice.value === StartDateEnum.TODAY
          ? TODAY_DATE
          : selectChoice.value === StartDateEnum.TOMORROW
          ? TOMORROW_DATE
          : startDate.value
    }

    return { page, answers }
  }
</script>

<style lang="scss" scoped>
  .DatePickerComponent {
    margin-top: $spacing-30;
  }
</style>
